/**
 * Upload with Progress Utility
 *
 * Provides file upload functionality with real-time progress tracking
 * using XMLHttpRequest instead of fetch() which doesn't support upload progress.
 *
 * @module lib/uploadWithProgress
 */

export interface UploadOptions {
  /** URL to upload to */
  url: string;
  /** FormData to upload */
  formData: FormData;
  /** Authorization token (optional) */
  token?: string | null;
  /** Progress callback - called with percentage (0-100) */
  onProgress?: (progress: number) => void;
  /** Abort signal for cancellation */
  signal?: AbortSignal;
}

export interface UploadResult<T> {
  /** Whether the upload was successful */
  ok: boolean;
  /** HTTP status code */
  status: number;
  /** Response data (if successful) */
  data?: T;
  /** Error message (if failed) */
  error?: string;
  /** Error code from backend (e.g. 'STORAGE_LIMIT_EXCEEDED', 'TABLE_LIMIT_EXCEEDED') */
  errorCode?: string;
}

/**
 * Upload a file with progress tracking using XMLHttpRequest
 *
 * @param options - Upload configuration
 * @returns Promise resolving to upload result
 *
 * @example
 * ```typescript
 * const result = await uploadWithProgress<TableCreateResponse>({
 *   url: `${API_BASE_URL}/api/private/uploads/create-table`,
 *   formData,
 *   token: await getToken(),
 *   onProgress: (progress) => setUploadProgress(progress),
 * });
 *
 * if (result.ok && result.data) {
 *   console.log('Uploaded:', result.data.table.id);
 * }
 * ```
 */
export function uploadWithProgress<T>(options: UploadOptions): Promise<UploadResult<T>> {
  const { url, formData, token, onProgress, signal } = options;

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    // Handle abort signal
    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        resolve({
          ok: false,
          status: 0,
          error: 'Upload cancelled',
        });
      });
    }

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        onProgress(percentage);
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText) as T;
          resolve({
            ok: true,
            status: xhr.status,
            data,
          });
        } catch {
          resolve({
            ok: false,
            status: xhr.status,
            error: 'Failed to parse response',
          });
        }
      } else {
        // Try to extract error message and code from response
        let errorMessage = `Upload failed with status ${xhr.status}`;
        let errorCode: string | undefined;
        try {
          const errorData = JSON.parse(xhr.responseText) as { error?: string; message?: string; code?: string };
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          if (errorData.code) {
            errorCode = errorData.code;
          }
        } catch {
          // Use default error message
        }
        resolve({
          ok: false,
          status: xhr.status,
          error: errorMessage,
          errorCode,
        });
      }
    });

    // Handle network errors
    xhr.addEventListener('error', () => {
      resolve({
        ok: false,
        status: 0,
        error: 'Network error during upload',
      });
    });

    // Handle timeout
    xhr.addEventListener('timeout', () => {
      resolve({
        ok: false,
        status: 0,
        error: 'Upload timed out',
      });
    });

    // Configure and send request
    xhr.open('POST', url);
    xhr.withCredentials = true;

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
}
